import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  SupportedLanguage,
  SUPPORTED_LANGUAGES,
  translateKey,
} from './translations';
import { auth, syncUserProfileToFirestore } from '../lib/firebase';

export const LANGUAGE_STORAGE_KEY = 'pairup_language';

export function detectInitialLanguage(): SupportedLanguage {
  if (typeof window !== 'undefined') {
    try {
      const saved = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (saved === 'en' || saved === 'bn' || saved === 'hi') {
        return saved;
      }
    } catch {
      // ignore storage errors
    }

    if (typeof navigator !== 'undefined') {
      const candidates = [
        ...(Array.isArray(navigator.languages) ? navigator.languages : []),
        navigator.language || '',
      ];
      for (const raw of candidates) {
        const lower = String(raw || '').toLowerCase();
        if (lower.startsWith('bn')) return 'bn';
        if (lower.startsWith('hi')) return 'hi';
        if (lower.startsWith('en')) return 'en';
      }
    }
  }
  return 'en';
}

export interface LanguageContextValue {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: (key: string, fallback?: string) => string;
  supportedLanguages: typeof SUPPORTED_LANGUAGES;
}

const LanguageContext = createContext<LanguageContextValue>({
  language: 'en',
  setLanguage: () => undefined,
  t: (key: string, fallback?: string) => translateKey('en', key, fallback),
  supportedLanguages: SUPPORTED_LANGUAGES,
});

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(() => detectInitialLanguage());

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [language]);

  const setLanguage = useCallback((nextLang: SupportedLanguage) => {
    if (nextLang !== 'en' && nextLang !== 'bn' && nextLang !== 'hi') return;
    setLanguageState(nextLang);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLang);
      } catch {
        // ignore storage errors
      }
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = nextLang;
    }
    // Persist language preference to Firestore user profile when authenticated
    if (auth?.currentUser) {
      syncUserProfileToFirestore({
        uid: auth.currentUser.uid,
        preferredLanguage: nextLang,
      } as any).catch(() => undefined);
    }
  }, []);

  const t = useCallback(
    (key: string, fallback?: string) => translateKey(language, key, fallback),
    [language]
  );

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        t,
        supportedLanguages: SUPPORTED_LANGUAGES,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export function useLanguage(): LanguageContextValue {
  return useContext(LanguageContext);
}
